import { ConnectionPost, Post } from "../models/Post";

type PostStatus = Post["status"];

/**
 * Derives the overall post status based on the states of its connections.
 *
 * States:
 * - "draft": No connections, or only pending connections without scheduled tasks
 * - "scheduled": Has pending connections with scheduled tasks (taskName set)
 * - "publishing": Any connection is actively publishing
 * - "pre-processing": Any connection is pre-processing (and none publishing)
 * - "published": All connections are published
 * - "failed": All connections failed
 * - "mixed": Mix of published and failed (terminal states only)
 */
export function derivePostStatus(
  connections: Record<string, ConnectionPost> | undefined
): PostStatus {
  const values = Object.values(connections || {});
  if (values.length === 0) return "draft";

  const allPublished = values.every((c) => c.status === "published");
  const allFailed = values.every((c) => c.status === "failed");
  const hasPending = values.some((c) => c.status === "pending");

  if (allPublished) return "published";
  if (allFailed) return "failed";
  if (values.some((c) => c.status === "publishing")) return "publishing";
  if (values.some((c) => c.status === "pre-processing")) return "pre-processing";
  if (hasPending) {
    const hasScheduledPending = values.some(
      (c) => c.status === "pending" && (c.taskName || c.deferred)
    );
    return hasScheduledPending ? "scheduled" : "draft";
  }
  return "mixed"; // published + failed
}

/**
 * Whether a post can be edited.
 *
 * Editable when:
 * - Has pending connections OR is a draft (no connections)
 * - Not actively processing (publishing / pre-processing)
 * - Not currently generating
 */
export function isPostEditable(post: Pick<Post, "status" | "connections">): boolean {
  const connections = Object.values(post.connections || {});
  if (post.status === "generating") return false;
  const isProcessing = connections.some(
    (c) => c.status === "publishing" || c.status === "pre-processing"
  );
  if (isProcessing) return false;
  const hasPending = connections.some((c) => c.status === "pending");
  return hasPending || connections.length === 0;
}
